import { type Dispatch, type SetStateAction } from 'react';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import type {
  ContributorDto,
  EventContributorRole,
  FlowContributorRole,
  RoleDto,
  SessionContributorRole,
} from '@/types/dtos/contributors/response/contributor.dto.ts';
import usePatchContributorRoleMutation from '@/state/mutations/contributors/use-patch-contributor-role';

const RequestContributorMediaModal = ({
  open,
  setOpen,
  entity,
  entityType,
  contributor,
  role,
  isMediaRequested,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  entity: { id: string; version: number; title: string };
  entityType: 'Session' | 'Flow';
  contributor?: ContributorDto | { contributor: ContributorDto };
  role: RoleDto<EventContributorRole | FlowContributorRole | SessionContributorRole>;
  isMediaRequested: boolean;
}) => {
  const t = useTranslations();
  const notification = useNotifications();
  const { mutateAsync: updateContributorRole, isPending: updateContributorRolePending } =
    usePatchContributorRoleMutation();

  const handleConfirm = async () => {
    if (!contributor) return;

    const isFlowContributor = 'contributor' in contributor;
    const actualContributor = isFlowContributor ? contributor.contributor : contributor;
    const currentIsMediaRequested = isMediaRequested;

    try {
      const payload = {
        contributorId: actualContributor.id,
        isMediaRequested: !currentIsMediaRequested,
        role: role.role,
        roleId: role.id,
      };
      await updateContributorRole(payload);

      notification.notifySuccess(
        t(
          currentIsMediaRequested
            ? 'contributor.notification.mediaRequestCancelSuccess'
            : 'contributor.notification.mediaRequestSuccess',
          { name: actualContributor.profiles[0]?.displayName },
        ),
      );
      setOpen(false);
    } catch {
      notification.notifyError(
        t(
          currentIsMediaRequested
            ? 'contributor.notification.mediaRequestCancelError'
            : 'contributor.notification.mediaRequestError',
          { name: actualContributor.profiles[0]?.displayName },
        ),
      );
    }
  };

  return (
    <>
      <ConfirmationModal
        open={open}
        isLoading={updateContributorRolePending}
        onConfirm={handleConfirm}
        onCancel={() => setOpen(false)}
        title={t(
          (() => {
            if (!contributor) return 'contributor.requestMediaModal.requestTitle';
            const currentIsMediaRequested = isMediaRequested;
            return currentIsMediaRequested
              ? 'contributor.requestMediaModal.cancelTitle'
              : 'contributor.requestMediaModal.requestTitle';
          })(),
          {
            name: (() => {
              if (!contributor) return '';
              const isFlowContributor = 'contributor' in contributor;
              const actualContributor = isFlowContributor ? contributor.contributor : contributor;
              return actualContributor.profiles[0]?.displayName ?? '';
            })(),
          },
        )}
        text={t(
          (() => {
            if (!contributor) return 'contributor.requestMediaModal.requestText';
            const currentIsMediaRequested = isMediaRequested;
            return currentIsMediaRequested
              ? 'contributor.requestMediaModal.cancelText'
              : 'contributor.requestMediaModal.requestText';
          })(),
          {
            type: entityType === 'Session' ? t('session.title') : t('common.flow'),
            title: entity.title,
            name: (() => {
              if (!contributor) return '';
              const isFlowContributor = 'contributor' in contributor;
              const actualContributor = isFlowContributor ? contributor.contributor : contributor;
              return actualContributor.profiles[0]?.displayName ?? '';
            })(),
          },
        )}
      />
    </>
  );
};

export default RequestContributorMediaModal;
